import React, { useState, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { css } from 'emotion';

import debounce from 'lodash/debounce';
import Input from '@vromo/ui/components/Input';
import { selectStyle } from '@vromo/ui/components/Select2';
import AsyncSelect from 'react-select/async';
import { GroupTypeBase, mergeStyles, OptionTypeBase, NamedProps } from 'react-select';

import { Coords } from '@vromo/models';

const search = debounce((searchTerm, callback, proximity) => {
    if (searchTerm) {
        fetch(
            `https://2mwssqe7ta.execute-api.us-east-1.amazonaws.com/prod/autocomplete?input=${searchTerm}${
                proximity ? `&proximity=${proximity.lat},${proximity.lon}` : ``
            }`
        )
            .then(async (result) => {
                const { predictions } = await result.json();
                callback(predictions.map((it) => ({ label: it.description, value: it })));
            })
            .catch((error) => {
                console.error(error);
                callback(null);
            });
    }
}, 300);

const getDetails = async (place: any) => {
    const url = `https://2mwssqe7ta.execute-api.us-east-1.amazonaws.com/prod/details?placeid=${place.place_id}`;
    const response = await fetch(url);
    const {
        result: {
            geometry: {
                location: { lat, lng: lon }
            }
        }
    } = await response.json();
    return { center: [lon, lat], place_name: place.description };
};

export type GeocoderValue = { label: string; value: Coords };

type Props = {
    value: GeocoderValue | null;
    onChange: (value: GeocoderValue | null) => void;
    label?: string;
    placeholder?: string;
    proximity?: Coords;
    maxMenuHeight?: number;
    disabled?: boolean;
    onInputChange?: NamedProps['onInputChange'];
    required?: boolean;
};

export default observer(function Geocoder({
    value,
    label,
    onChange,
    placeholder = 'Search',
    proximity,
    maxMenuHeight = 800,
    onInputChange,
    required
}: Props) {
    const [isLoading, setIsLoading] = useState(false);
    const [isDisabled, setIsDisabled] = useState(false);
    const ref = useRef<AsyncSelect<OptionTypeBase, false, GroupTypeBase<OptionTypeBase>>>(null);

    return (
        <>
            {label ? <label>{label}</label> : null}
            <AsyncSelect
                onInputChange={onInputChange}
                ref={ref}
                value={value}
                placeholder={placeholder}
                onChange={(selected: any) => {
                    if (!selected) {
                        onChange(null);
                    } else {
                        setIsDisabled(true);
                        setIsLoading(true);

                        getDetails(selected.value).then((res) => {
                            const [lon, lat] = res.center;
                            onChange({ label: res.place_name, value: new Coords(lat, lon) });

                            setIsDisabled(false);
                            setIsLoading(false);
                        });
                    }
                }}
                loadOptions={(inputValue: string, callback: any) =>
                    search(inputValue, callback, proximity)
                }
                isClearable
                noOptionsMessage={({ inputValue }) =>
                    inputValue?.length
                        ? `We couldn't find any places that matched that description.`
                        : `Start typing to search for an address or place.`
                }
                styles={mergeStyles(selectStyle, {
                    dropdownIndicator: (styles: any) => ({ ...styles, display: 'none' })
                })}
                menuPosition={'fixed'}
                menuPlacement={'bottom'}
                maxMenuHeight={maxMenuHeight}
                menuPortalTarget={document.body}
                isDisabled={isDisabled}
                isLoading={isLoading}
                blurInputOnSelect={true}
            />
            {!isDisabled && (
                <div style={{ position: 'relative' }}>
                    <input
                        // HACK https://github.com/JedWatson/react-select/issues/4327
                        tabIndex={-1}
                        autoComplete="off"
                        style={{
                            opacity: 0,
                            width: '100%',
                            height: 0,
                            position: 'absolute'
                        }}
                        value={value?.label}
                        onFocus={() => ref.current?.focus}
                        required={required}
                    />
                </div>
            )}
        </>
    );
});
